import { useEffect } from 'react'
import { Link } from 'react-router-dom'

/**
 * Public Privacy Policy page rendered as HTML so Google's branding/OAuth
 * verifier can crawl it. The text content mirrors OVACS_Privacy_Policy.pdf —
 * keep both in sync when one changes.
 *
 * URL: https://ovacs.com/privacy
 */
const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy — OVACS'
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta')
      m.name = 'description'
      document.head.appendChild(m)
      return m
    })()
    meta.content =
      'OVACS Privacy Policy — how we collect, use, share, and protect your personal data when you use the OVACS case-management platform.'
    return () => { document.title = 'OVACS' }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-semibold text-gray-900">
            <span className="text-cyan-600">OVACS</span>
          </Link>
          <nav className="text-sm text-gray-600 flex items-center gap-5">
            <Link to="/landing" className="hover:text-cyan-700">Home</Link>
            <Link to="/terms" className="hover:text-cyan-700">Terms</Link>
            <Link to="/contact" className="hover:text-cyan-700">Contact</Link>
          </nav>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-2"><strong>Last updated:</strong> 11 May 2026</p>
        <p className="text-sm text-gray-500 mb-10"><strong>Effective date:</strong> 11 May 2026</p>

        {/* Summary callout */}
        <div className="bg-cyan-50 border-l-4 border-cyan-500 p-5 rounded mb-10 text-sm text-gray-800 leading-relaxed">
          <p className="font-semibold text-gray-900 mb-2">Plain-language summary</p>
          <p>
            OVACS is a case-management platform. To make it work, we collect the email and basic profile information of every user, and we store the case files, documents, voice messages, and notes that you upload into your workspace. We never sell your data. We never advertise. We only share data with the small set of vendors that keep the service running (hosting, email, push notifications) and — if you choose to enable it — with the cloud storage provider you connect (e.g. Google Drive). You can export your data, correct it, or delete your account at any time.
          </p>
          <p className="mt-2">
            The full policy below explains exactly what data we collect, why, how long we keep it, and what your legal rights are. If anything in this document is unclear, write to us at <a className="text-cyan-700 underline" href="mailto:info@ovacs.com">info@ovacs.com</a>.
          </p>
        </div>

        <Section title="1. About this policy">
          <p>
            This Privacy Policy explains how OVACS (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects, uses, shares, and protects personal data when you use the OVACS application and related websites (collectively, the &ldquo;Service&rdquo;) accessible at <a className="text-cyan-700 underline" href="https://ovacs.com">https://ovacs.com</a> and via our mobile applications.
          </p>
          <p>
            We are the <strong>data controller</strong> for personal data described in this policy, except where we act as a <strong>data processor</strong> on behalf of the workspace owner who invited you. See Section 4 below.
          </p>
          <p>
            If you have a question or want to exercise your rights, contact us at <a className="text-cyan-700 underline" href="mailto:info@ovacs.com">info@ovacs.com</a>.
          </p>
        </Section>

        <Section title="2. Scope">
          <ul className="list-disc list-inside space-y-1">
            <li>Visitors of https://ovacs.com and any subdomains</li>
            <li>Account holders (individual users, workspace owners, invited members)</li>
            <li>People whose information is uploaded into OVACS by an account holder (e.g. clients of a law firm — see Section 4)</li>
            <li>Users of any official OVACS mobile application</li>
          </ul>
          <p>
            It does <strong>not</strong> apply to third-party services you reach via outbound links (e.g. Google&rsquo;s privacy policy when you connect Google Drive). Those services have their own policies and we are not responsible for them.
          </p>
        </Section>

        <Section title="3. What information we collect">
          <SubHeading>3.1 Information you provide</SubHeading>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Account profile:</strong> full name, email, phone number (with country code), profile picture (optional), short bio (optional), preferred language, role, and date of registration.</li>
            <li><strong>Authentication credentials:</strong> a hashed (irreversible) form of your password. We never store passwords in plain text.</li>
            <li><strong>Workspace metadata:</strong> workspace name, workspace type (legal / business / other), and custom terminology you configure.</li>
            <li><strong>Subscription &amp; billing data:</strong> plan tier, subscription status, and (if applicable) last four digits and expiry month of the payment card on file. Full card numbers are never stored on OVACS servers — they are tokenized by our payment processor.</li>
            <li><strong>Communications:</strong> the contents of support tickets, contact-form submissions, and emails you send us.</li>
          </ul>

          <SubHeading>3.2 Workspace content you upload</SubHeading>
          <p>The following content is stored in your workspace when you use OVACS:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Client records:</strong> name, contact details, identifiers, country, and any custom fields.</li>
            <li><strong>Case / matter records:</strong> titles, descriptions, statuses, dates, security levels, tags, assigned users.</li>
            <li><strong>Session records:</strong> date, time, duration, notes, attached documents.</li>
            <li><strong>Documents:</strong> files you upload (any file type up to the per-plan size limit), filenames, metadata, MIME types, access history.</li>
            <li><strong>Text messages:</strong> body of internal messages.</li>
            <li><strong>Voice messages:</strong> audio files recorded inside OVACS, with metadata.</li>
            <li><strong>Activity &amp; read-receipts:</strong> which user viewed which document, when, from which IP — for security audit.</li>
          </ul>
          <p>This is collectively referred to as <strong>&ldquo;Workspace Content&rdquo;</strong>.</p>

          <SubHeading>3.3 Information collected automatically</SubHeading>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Technical logs:</strong> IP address, user-agent, request paths, response codes, timestamps. Retained up to 90 days for security and abuse prevention.</li>
            <li><strong>Authentication events:</strong> login timestamps, session start/end, JWT issuance, password resets, failed login attempts.</li>
            <li><strong>Document access audit log:</strong> user ID, document ID, action, security level, source IP, access reason.</li>
            <li><strong>Cloud storage audit log:</strong> connect/disconnect events, upload outcomes, auth-token-revoked events, quota-exceeded events. We log events, not file contents.</li>
            <li><strong>Push notification tokens</strong> (mobile only): Firebase Cloud Messaging tokens, so we can deliver notifications.</li>
          </ul>

          <SubHeading>3.4 What we do NOT collect</SubHeading>
          <ul className="list-disc list-inside space-y-1">
            <li>No third-party advertising trackers, pixels, or cross-site analytics SDKs.</li>
            <li>We do not sell, rent, or trade personal data.</li>
            <li>We do not read your Workspace Content for advertising, public-model training, or any commercial purpose unrelated to delivering the Service to you.</li>
            <li>No special-category data unless you choose to upload such data as Workspace Content. In that case, you are the controller of that content (Section 4).</li>
          </ul>

          <SubHeading>3.5 Cookies and similar technologies</SubHeading>
          <p>We use a minimal set of functional cookies and local-storage tokens — no advertising or third-party analytics cookies.</p>
          <div className="overflow-x-auto mt-3">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 border-b">Name / category</th>
                  <th className="text-left px-3 py-2 border-b">Purpose</th>
                  <th className="text-left px-3 py-2 border-b">Retention</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <Row name="Authentication JWT (access token)" purpose="Keeps you signed in" retention="7 days, auto-refreshed" />
                <Row name="Authentication JWT (refresh token)" purpose="Issues new access tokens without re-login" retention="7 days" />
                <Row name="CSRF token" purpose="Protects admin panel against cross-site request forgery" retention="Session" />
                <Row name="Session cookie" purpose="Django admin only (not the main app)" retention="Session" />
                <Row name="Workspace context (local storage)" purpose="Remembers your last viewed workspace" retention="Until sign-out" />
                <Row name="Theme / UI preferences (local storage)" purpose="Language and interface preferences" retention="Until you clear browser data" />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="4. Roles — when we are a controller vs a processor">
          <p>Under GDPR (and equivalent laws), our role depends on the data:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Controller</strong> for: account profile data, technical logs, authentication events, communications you send us.</li>
            <li><strong>Processor</strong> for: Workspace Content. The workspace owner is the controller. We only process this data on their documented instructions.</li>
          </ul>
          <p>
            If you are an <strong>invited workspace member</strong>, your workspace owner is responsible for telling you why your data was uploaded and on what legal basis. If you are a <strong>client of a firm</strong> whose data was uploaded into OVACS, please contact that firm directly to exercise your rights regarding that content — we will assist them in fulfilling your request.
          </p>
        </Section>

        <Section title="5. Why we use your information (purposes & legal bases)">
          <p>Under GDPR Article 6, we rely on the following legal bases:</p>
          <div className="overflow-x-auto mt-3">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 border-b">Purpose</th>
                  <th className="text-left px-3 py-2 border-b">Legal basis (GDPR Art. 6)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <PurposeRow p="Creating and authenticating your account; delivering core features" b="Contract — Art. 6(1)(b)" />
                <PurposeRow p="Transactional emails (password reset, security alerts, billing receipts, invitations)" b="Contract — Art. 6(1)(b)" />
                <PurposeRow p="Processing subscription payments" b="Contract — Art. 6(1)(b)" />
                <PurposeRow p="Security: rate limiting, abuse detection, audit logging, breach prevention" b="Legitimate interests — Art. 6(1)(f)" />
                <PurposeRow p="Complying with tax, accounting, or legal-discovery obligations" b="Legal obligation — Art. 6(1)(c)" />
                <PurposeRow p="Optional product-update emails (non-transactional)" b="Consent — Art. 6(1)(a). Withdraw anytime." />
                <PurposeRow p="Mobile push notifications" b="Consent at OS level — you control via device settings." />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="6. Cloud storage integrations (Google Drive, etc.)">
          <p>OVACS offers an optional integration that lets the workspace owner connect their own cloud storage account (currently Google Drive; OneDrive and Dropbox planned). When enabled:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>OVACS only accesses files it creates.</strong> We use the OAuth scope <code className="bg-gray-100 px-1 rounded text-xs">https://www.googleapis.com/auth/drive.file</code>, which restricts access to files and folders the OVACS app itself created. We cannot see, list, or modify the rest of your Drive.</li>
            <li><strong>Your files leave the OVACS server.</strong> New uploads (after connection) are written directly to your cloud account, into a folder named <code className="bg-gray-100 px-1 rounded text-xs">OVACS/&#123;workspace name&#125;/</code>. Existing files uploaded before the integration stay on OVACS storage unless and until you migrate them.</li>
            <li><strong>We store the OAuth refresh token encrypted at rest</strong> using the Fernet cipher, with the key held only on our servers. The token lets OVACS upload to and read from your Drive on your behalf. You can revoke it at any time from the Cloud Storage settings page or from your Google Account → Security → Third-party app access.</li>
            <li><strong>We store metadata about your files</strong> — filename, size, MIME type, the Drive file_id, security level, access audit entries — in the OVACS database so the app can list and search documents. We do not mirror file contents.</li>
            <li><strong>If you disconnect:</strong> the OAuth token is revoked and deleted from our database. Files already in your Drive are not deleted by OVACS — they remain in your <code className="bg-gray-100 px-1 rounded text-xs">OVACS/</code> folder, under your control.</li>
          </ul>
          <p className="mt-3 text-sm bg-yellow-50 border border-yellow-200 p-3 rounded">
            <strong>Important risks before connecting:</strong> if the cloud account becomes inaccessible (deleted, suspended, password lost, owner leaves), all OVACS files in that account become inaccessible to your workspace until access is restored. We recommend connecting an account owned by the firm/organisation, not a personal account.
          </p>
          <p>The cloud provider applies its own privacy policy once your files are in your storage. For Google Drive, see <a className="text-cyan-700 underline" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a>.</p>
        </Section>

        <Section title="7. Who we share your information with">
          <p>We share personal data only with the limited set of sub-processors that help us run the Service, and only as needed.</p>
          <div className="overflow-x-auto mt-3">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 border-b">Sub-processor</th>
                  <th className="text-left px-3 py-2 border-b">Purpose</th>
                  <th className="text-left px-3 py-2 border-b">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <SubProcRow n="Hostinger (VPS hosting)" p="Application hosting, MySQL database, file storage" l="EU / global edge" />
                <SubProcRow n="Hostinger Email (SMTP)" p="Outgoing transactional email" l="EU" />
                <SubProcRow n="Google LLC (Firebase Cloud Messaging)" p="Mobile push notifications (title + short body only)" l="Global" />
                <SubProcRow n="Google LLC (Drive API)" p="Storage of your Workspace Content files — only if you connect Google Drive" l="Per your Google account settings" />
                <SubProcRow n="Payment processor" p="Subscription billing" l="EU / US (under SCCs)" />
              </tbody>
            </table>
          </div>
          <p className="mt-3">
            We may add or change sub-processors. Material changes will be reflected here and (for changes that materially expand data sharing) communicated to workspace owners by email at least 30 days in advance.
          </p>
          <p>
            We may also disclose information if required by law (court order, regulatory request, lawful subpoena) or to protect the safety, rights, or property of OVACS, our users, or the public. Where lawfully possible, we will notify the affected user before complying.
          </p>
          <p><strong>We do not sell personal data, and we do not share it with advertisers or data brokers.</strong></p>
        </Section>

        <Section title="8. International data transfers">
          <p>OVACS is operated from Sweden. Our primary servers are located in the European Economic Area (EEA). Some sub-processors (e.g. Firebase, payment processor) may process data outside the EEA, including the United States.</p>
          <p>When personal data is transferred outside the EEA, we rely on:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>EU Standard Contractual Clauses (SCCs) approved by the European Commission, or</li>
            <li>The EU–US Data Privacy Framework (where the vendor is certified), or</li>
            <li>Another transfer mechanism approved under GDPR Chapter V.</li>
          </ul>
          <p>You can request a copy of the safeguards in place by emailing <a className="text-cyan-700 underline" href="mailto:info@ovacs.com">info@ovacs.com</a>.</p>
        </Section>

        <Section title="9. How long we keep your data">
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 border-b">Data type</th>
                  <th className="text-left px-3 py-2 border-b">Retention period</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <RetRow d="Account profile (active accounts)" r="For as long as your account exists" />
                <RetRow d="Account profile (after deletion)" r="30 days soft-deleted, then permanent deletion" />
                <RetRow d="Workspace Content" r="For as long as your workspace exists; 30 days soft-deleted on workspace deletion" />
                <RetRow d="Technical logs (IP, request logs)" r="90 days" />
                <RetRow d="Authentication & security audit logs" r="12 months" />
                <RetRow d="Document access audit logs" r="24 months (legal evidence retention)" />
                <RetRow d="Billing records" r="7 years (tax / accounting obligation)" />
                <RetRow d="Support correspondence" r="24 months from last reply" />
              </tbody>
            </table>
          </div>
          <p className="mt-3">After the retention period, data is either deleted or fully anonymized so the individual can no longer be re-identified.</p>
        </Section>

        <Section title="10. How we protect your data">
          <p>We apply technical and organisational security measures appropriate to the sensitivity of the data we process:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Encryption in transit:</strong> all traffic served over HTTPS (TLS 1.2+) with HSTS enforced.</li>
            <li><strong>Encryption at rest:</strong> OAuth refresh tokens encrypted with Fernet; database backups encrypted at the storage layer.</li>
            <li><strong>Hashed passwords:</strong> industry-standard algorithms, no plain-text storage.</li>
            <li><strong>JWT authentication</strong> with short access-token lifetimes (7 days) and rotating refresh tokens.</li>
            <li><strong>Per-document signed access URLs</strong> with HMAC-SHA256 signatures and short expiry.</li>
            <li><strong>Role-based access control</strong> (RBAC), scoped per workspace, role, and document security level.</li>
            <li><strong>Audit logging</strong> of every document access, cloud-storage event, authentication event, and admin action.</li>
            <li><strong>Rate limiting</strong> on authentication endpoints to prevent brute-force attacks.</li>
            <li><strong>Principle of least privilege</strong> for staff; production access gated by SSH-key authentication.</li>
            <li><strong>Regular dependency updates</strong> to address known vulnerabilities.</li>
            <li><strong>Backups:</strong> daily database backups (7-day retention) + weekly backups (4-week retention).</li>
          </ul>
          <p className="mt-3">No method of transmission or storage is 100% secure. If a personal data breach occurs that is likely to result in a risk to your rights and freedoms, we will notify the relevant supervisory authority within 72 hours as required by GDPR Article 33, and notify affected users without undue delay as required by Article 34.</p>
        </Section>

        <Section title="11. Your rights">
          <p>If you are in the EEA, UK, or another jurisdiction with equivalent law, you have the following rights:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Right to access</strong> — request a copy of the personal data we hold about you.</li>
            <li><strong>Right to rectification</strong> — ask us to correct inaccurate data.</li>
            <li><strong>Right to erasure</strong> (&ldquo;right to be forgotten&rdquo;) — ask us to delete your data, subject to legal-retention obligations.</li>
            <li><strong>Right to restriction</strong> — ask us to limit how we process your data.</li>
            <li><strong>Right to data portability</strong> — receive your data in a structured, machine-readable format (we provide JSON exports).</li>
            <li><strong>Right to object</strong> — object to processing based on our legitimate interests.</li>
            <li><strong>Right to withdraw consent</strong> — for any consent-based processing, at any time.</li>
            <li><strong>Right not to be subject to solely automated decisions</strong> — OVACS does not make decisions about you using solely automated processing.</li>
            <li><strong>Right to lodge a complaint</strong> with a supervisory authority — in Sweden, this is <a className="text-cyan-700 underline" href="https://www.imy.se" target="_blank" rel="noopener noreferrer">IMY (Integritetsskyddsmyndigheten)</a>.</li>
          </ul>
          <p className="mt-3">To exercise any of these rights, email <a className="text-cyan-700 underline" href="mailto:info@ovacs.com">info@ovacs.com</a> from the address associated with your OVACS account, or use the in-app Settings → Account → Data Export / Delete Account flows. We respond within 30 days (extendable by 60 days for complex requests, with notice). No fee for the first request in any 12-month period.</p>
        </Section>

        <Section title="12. Workspace members and clients">
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Workspace members:</strong> the workspace owner who invited you decides what data to upload about you and why. Direct requests about Workspace Content to that workspace owner. Requests about your individual account profile (email, password, login history) go to OVACS.</li>
            <li><strong>Clients whose data was uploaded by a firm:</strong> the firm is the controller for that data. Please contact the firm directly. If you believe OVACS is improperly processing your data, you can still write to info@ovacs.com and we will assist the firm in fulfilling your rights.</li>
          </ul>
        </Section>

        <Section title="13. Children">
          <p>OVACS is <strong>not intended for use by anyone under 16</strong>. We do not knowingly collect personal data from children under 16. If you believe a child has provided us with personal data, please contact <a className="text-cyan-700 underline" href="mailto:info@ovacs.com">info@ovacs.com</a> and we will delete the account.</p>
        </Section>

        <Section title="14. Marketing communications">
          <p>We only send marketing or product-update emails to people who explicitly opt in. You can opt out at any time via the unsubscribe link in any such email or by emailing <a className="text-cyan-700 underline" href="mailto:info@ovacs.com">info@ovacs.com</a>. Opting out of marketing does not stop transactional emails (password resets, billing receipts, security alerts).</p>
        </Section>

        <Section title="15. Changes to this policy">
          <p>We may update this policy from time to time. When we do, we will:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Update the &ldquo;Last updated&rdquo; date at the top.</li>
            <li>For material changes, notify all workspace owners by email at least 30 days before the change takes effect, and post an in-app notice.</li>
            <li>Keep an archive of previous versions, available on request.</li>
          </ul>
          <p>Continuing to use OVACS after a change becomes effective constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="16. Contact us">
          <p>For privacy questions, requests, or complaints:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Email:</strong> <a className="text-cyan-700 underline" href="mailto:info@ovacs.com">info@ovacs.com</a></li>
            <li><strong>Subject line:</strong> &ldquo;Privacy request — [your name]&rdquo;</li>
          </ul>
        </Section>

        <hr className="my-12 border-gray-200" />
        <p className="text-xs text-gray-500">
          A PDF version of this policy is also available at{' '}
          <a className="text-cyan-700 underline" href="/OVACS_Privacy_Policy.pdf" target="_blank" rel="noopener noreferrer">
            /OVACS_Privacy_Policy.pdf
          </a>.
        </p>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-6 text-sm text-gray-500 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} OVACS. All rights reserved.</div>
          <nav className="flex items-center gap-4">
            <Link to="/landing" className="hover:text-cyan-700">Home</Link>
            <Link to="/privacy" className="hover:text-cyan-700">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-cyan-700">Terms</Link>
            <Link to="/contact" className="hover:text-cyan-700">Contact</Link>
          </nav>
        </div>
      </footer>
    </div>
  )
}

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 scroll-mt-20" id={slugify(title)}>{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed text-[15px]">
      {children}
    </div>
  </section>
)

const SubHeading = ({ children }) => (
  <h3 className="text-base font-semibold text-gray-900 mt-5 mb-1">{children}</h3>
)

const Row = ({ name, purpose, retention }) => (
  <tr>
    <td className="px-3 py-2 align-top">{name}</td>
    <td className="px-3 py-2 align-top">{purpose}</td>
    <td className="px-3 py-2 align-top">{retention}</td>
  </tr>
)

const PurposeRow = ({ p, b }) => (
  <tr>
    <td className="px-3 py-2 align-top">{p}</td>
    <td className="px-3 py-2 align-top">{b}</td>
  </tr>
)

const SubProcRow = ({ n, p, l }) => (
  <tr>
    <td className="px-3 py-2 align-top">{n}</td>
    <td className="px-3 py-2 align-top">{p}</td>
    <td className="px-3 py-2 align-top">{l}</td>
  </tr>
)

const RetRow = ({ d, r }) => (
  <tr>
    <td className="px-3 py-2 align-top">{d}</td>
    <td className="px-3 py-2 align-top">{r}</td>
  </tr>
)

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

export default PrivacyPolicyPage
